import * as React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
} from '@mui/material';
import {
  ACCENT,
  GRAY_700,
  GRAY_500,
  MONTHS,
  textFieldSx,
  selectSx,
  formLabelSx,
} from './PresentationWizard.styles';

function ScheduleStep({
  scheduleEnabled,
  setScheduleEnabled,
  recurrence,
  setRecurrence,
  scheduleDay,
  setScheduleDay,
  scheduleMonth,
  setScheduleMonth,
  scheduleTime,
  setScheduleTime,
  scheduleEmail,
  setScheduleEmail,
  customInterval,
  setCustomInterval,
  customUnit,
  setCustomUnit,
  validationErrors,
  setValidationErrors,
  timeIntervals,
}) {
  return (
    <>
      <Typography
        variant="body1"
        sx={{ color: '#64748b', marginBottom: '24px', fontSize: '16px' }}
      >
        Set up a recurring schedule to automatically generate and email this presentation.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={scheduleEnabled}
            onChange={(e) => setScheduleEnabled(e.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ACCENT },
            }}
          />
        }
        label={<Typography sx={{ fontSize: '0.925rem', fontWeight: 500, color: GRAY_700 }}>Enable Schedule</Typography>}
        sx={{ marginBottom: 3 }}
      />

      {scheduleEnabled && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Grid container spacing={2}>
            <Grid item size={4}>
              <FormControl fullWidth size="small" error={!!validationErrors.recurrence}>
                <InputLabel id="recurrence-label" sx={formLabelSx(!!validationErrors.recurrence)}>Recurrence</InputLabel>
                <Select
                  labelId="recurrence-label"
                  value={recurrence}
                  label="Recurrence"
                  sx={selectSx(!!validationErrors.recurrence)}
                  onChange={(e) => {
                    setRecurrence(e.target.value);
                    setScheduleDay('');
                    setScheduleMonth('');
                    if (validationErrors.recurrence) setValidationErrors((prev) => { const { recurrence: _, ...rest } = prev; return rest; });
                  }}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekdays">Daily (Weekdays only)</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {(recurrence === 'monthly' || recurrence === 'quarterly') && (
              <Grid item size={4}>
                <FormControl fullWidth size="small" error={!!validationErrors.scheduleDay}>
                  <InputLabel id="schedule-day-label" sx={formLabelSx(!!validationErrors.scheduleDay)}>Day of Month</InputLabel>
                  <Select
                    labelId="schedule-day-label"
                    value={scheduleDay}
                    label="Day of Month"
                    sx={selectSx(!!validationErrors.scheduleDay)}
                    onChange={(e) => {
                      setScheduleDay(e.target.value);
                      if (validationErrors.scheduleDay) setValidationErrors((prev) => { const { scheduleDay: _, ...rest } = prev; return rest; });
                    }}
                  >
                    {Array.from({ length: 31 }, (_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {recurrence === 'yearly' && (
              <>
                <Grid item size={4}>
                  <FormControl fullWidth size="small" error={!!validationErrors.scheduleMonth}>
                    <InputLabel id="schedule-month-label" sx={formLabelSx(!!validationErrors.scheduleMonth)}>Month</InputLabel>
                    <Select
                      labelId="schedule-month-label"
                      value={scheduleMonth}
                      label="Month"
                      sx={selectSx(!!validationErrors.scheduleMonth)}
                      onChange={(e) => {
                        setScheduleMonth(e.target.value);
                        if (validationErrors.scheduleMonth) setValidationErrors((prev) => { const { scheduleMonth: _, ...rest } = prev; return rest; });
                      }}
                    >
                      {MONTHS.map((m, i) => (
                        <MenuItem key={m} value={i + 1}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item size={4}>
                  <FormControl fullWidth size="small" error={!!validationErrors.scheduleDay}>
                    <InputLabel id="schedule-day-label" sx={formLabelSx(!!validationErrors.scheduleDay)}>Day</InputLabel>
                    <Select
                      labelId="schedule-day-label"
                      value={scheduleDay}
                      label="Day"
                      sx={selectSx(!!validationErrors.scheduleDay)}
                      onChange={(e) => {
                        setScheduleDay(e.target.value);
                        if (validationErrors.scheduleDay) setValidationErrors((prev) => { const { scheduleDay: _, ...rest } = prev; return rest; });
                      }}
                    >
                      {Array.from({ length: 31 }, (_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item size={4}>
              <FormControl fullWidth size="small" error={!!validationErrors.scheduleTime}>
                <InputLabel id="schedule-time-label" sx={formLabelSx(!!validationErrors.scheduleTime)}>Time</InputLabel>
                <Select
                  labelId="schedule-time-label"
                  value={scheduleTime}
                  label="Time"
                  sx={selectSx(!!validationErrors.scheduleTime)}
                  onChange={(e) => {
                    setScheduleTime(e.target.value);
                    if (validationErrors.scheduleTime) setValidationErrors((prev) => { const { scheduleTime: _, ...rest } = prev; return rest; });
                  }}
                >
                  {timeIntervals.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {recurrence === 'custom' && (
            <Grid container spacing={2} alignItems="center">
              <Grid item size={3}>
                <FormControl fullWidth size="small" error={!!validationErrors.scheduleDay}>
                  <InputLabel id="custom-day-label" sx={formLabelSx(!!validationErrors.scheduleDay)}>Day of Month</InputLabel>
                  <Select
                    labelId="custom-day-label"
                    value={scheduleDay}
                    label="Day of Month"
                    sx={selectSx(!!validationErrors.scheduleDay)}
                    onChange={(e) => {
                      setScheduleDay(e.target.value);
                      if (validationErrors.scheduleDay) setValidationErrors((prev) => { const { scheduleDay: _, ...rest } = prev; return rest; });
                    }}
                  >
                    {Array.from({ length: 31 }, (_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item size="auto">
                <Typography sx={{ color: GRAY_500, fontWeight: 500 }}>of every</Typography>
              </Grid>
              <Grid item size={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Interval"
                  value={customInterval}
                  onChange={(e) => {
                    setCustomInterval(Math.max(1, Number(e.target.value) || 1));
                    if (validationErrors.customInterval) setValidationErrors((prev) => { const { customInterval: _, ...rest } = prev; return rest; });
                  }}
                  error={!!validationErrors.customInterval}
                  helperText={validationErrors.customInterval}
                  slotProps={{ htmlInput: { min: 1 } }}
                  sx={textFieldSx(!!validationErrors.customInterval)}
                />
              </Grid>
              <Grid item size={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="custom-unit-label" sx={formLabelSx(false)}>Unit</InputLabel>
                  <Select
                    labelId="custom-unit-label"
                    value={customUnit}
                    label="Unit"
                    sx={selectSx(false)}
                    onChange={(e) => setCustomUnit(e.target.value)}
                  >
                    <MenuItem value="days">Day(s)</MenuItem>
                    <MenuItem value="weeks">Week(s)</MenuItem>
                    <MenuItem value="months">Month(s)</MenuItem>
                    <MenuItem value="quarters">Quarter(s)</MenuItem>
                    <MenuItem value="years">Year(s)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          <TextField
            fullWidth
            label="Email Destination"
            value={scheduleEmail}
            onChange={(e) => {
              setScheduleEmail(e.target.value);
              if (validationErrors.scheduleEmail) setValidationErrors((prev) => { const { scheduleEmail: _, ...rest } = prev; return rest; });
            }}
            placeholder="recipient@example.com"
            type="email"
            size="small"
            error={!!validationErrors.scheduleEmail}
            helperText={validationErrors.scheduleEmail}
            sx={textFieldSx(!!validationErrors.scheduleEmail)}
          />
        </Box>
      )}
    </>
  );
}

export default ScheduleStep;
